import type { ErrorMessageTemplate } from '@ricoh/ria.client.core/errors';
import type { MessageWithTitle } from '@ricoh/ria.client.core/types/component/data.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';
import type { i18n } from 'i18next';

import { BaseError, ServiceError } from '@ricoh/ria.client.core/errors';
import { isAxiosError } from 'axios';

import type {
  PrepareError,
  PrepareErrorMap,
  ServerErrorTemplate,
  ServiceServerErrorCode,
  ServiceServerErrorOptions,
  ServiceServerErrorResponseData,
} from './_error.type';

/**
 * ServiceServer Error.
 *
 * @template TEndpoint Request endpoint.
 */
export class ServiceServerError<TEndpoint extends string> extends ServiceError {
  /** Request endpoint key. */
  endpoint: TEndpoint;

  /**
   * Create ServiceServerError instance.
   *
   * @param {ServiceServerErrorOptions} options Options.
   */
  constructor({ endpoint, ...options }: ServiceServerErrorOptions<TEndpoint>) {
    super(options);

    this.endpoint = endpoint;
  }

  /**
   * Create Error instance if cause is not Error.
   *
   * @param {ServiceServerErrorOptions} options Options.
   * @param {PrepareError | undefined} prepareError Prepare error.
   *
   * @returns {BaseError} Error instance.
   */
  static override create<TEndpoint extends string>(options: ServiceServerErrorOptions<TEndpoint>, prepareError?: PrepareError): BaseError {
    if (options.cause instanceof BaseError) {
      return options.cause;
    }

    if (isAxiosError<Nullable<ServiceServerErrorResponseData>>(options.cause)) {
      const status = options.cause.status ?? options.status;

      if (!options.cause.response) {
        return new ServiceServerError<TEndpoint>({
          ...options,
          status,
        });
      }

      const [error] = options.cause.response.data?.errors ?? [];
      const message = error?.message ?? options.message;

      const errorTemplate =
        error?.code && prepareError
          ? prepareError({
              code: error.code,
              message: error.message,
              values: error.values,
            })
          : error;

      return new ServiceServerError<TEndpoint>({
        ...options,
        status: options.cause.response.data?.status ?? status,
        message,
        code: errorTemplate?.code,
        errorMessageTemplate: ServiceServerError.createErrorMessageTemplate(options.endpoint, errorTemplate),
      });
    }

    return new ServiceServerError<TEndpoint>(options);
  }

  /**
   * Get Error message.
   *
   * @param {i18n['t']} t Translate function.
   * @param {ServerErrorTemplate} template ServerError template.
   *
   * @returns {MessageWithTitle} Error message.
   */
  override errorMessage(t: i18n['t'], template?: ServerErrorTemplate): MessageWithTitle {
    if (!template) {
      return super.errorMessage(t);
    }

    const errorMessageTemplate = ServiceServerError.createErrorMessageTemplate(this.endpoint, template);
    return BaseError.resolveErrorMessageTemplate(t, errorMessageTemplate);
  }

  private static createErrorMessageTemplate(endpoint: string, errorTemplate: Nullable<Partial<ServerErrorTemplate>>): ErrorMessageTemplate | undefined {
    if (!errorTemplate?.code) {
      return undefined;
    }

    const { code, message = '', values = [] } = errorTemplate;
    if (code === 'Validation') {
      return {
        title: [`error:service.server.title.${endpoint}`],
        message,
        values: [],
      };
    }

    return {
      title: [`error:service.server.title.${endpoint}`],
      message: [`error:service.server.message.${endpoint}.${code}`, `server:api.${code}`],
      values,
    };
  }
}

/**
 * Create PrepareError.
 *
 * @template TRequestData Request data.
 * @template TCode Error code.
 * @param {PrepareErrorMap} map PrepareError map.
 *
 * @returns {Func<[requestData: TRequestData], PrepareError<TCode>>} PrepareError.
 */
export function createPrepareError<TRequestData = void, TCode extends ServiceServerErrorCode = ServiceServerErrorCode>(
  map: PrepareErrorMap<TRequestData, TCode>,
): Func<[requestData: TRequestData], PrepareError> {
  return (requestData) => {
    const errorMap = map(requestData);

    return (errorTemplate) => {
      const prepare = errorMap[errorTemplate.code as TCode];
      if (!prepare) {
        return errorTemplate;
      }

      return prepare(errorTemplate);
    };
  };
}
